import { Radio } from 'antd';
import { RotateCcwIcon } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';

import { CenterFlex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { CHART_TERM_TYPE } from '@/entities/chart/model/constants';
import useChartStateAction from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/useChartStateAction';
import { ChartTermTypeMap } from '@/features/(charts)/chart-renderer/model/chart-maps';

import { ChartDimensionSelect } from '../components/chart-dimension-select';
import ChartDataFilter from '../components/ChartDataFilter';
import ChartSeriesManager from '../components/ChartSeriesManager';
import ChartTermCompare from '../components/ChartTermCompare';
import ChartTermCustomPicker from '../components/ChartTermCustomPicker';
import ChartTermSelect from '../components/ChartTermSelect';
import { Pane } from '../components/ControllerPane';
import TermValidateDisplay from '../components/TermValidateDisplay';

type Props = {
  chartInfo: ChartContextProps;
  switchCount: number;
  setSwitchCount: Dispatch<SetStateAction<number>>;
};

export const CrossAxisController = (props: Props) => {
  const { chartInfo, switchCount, setSwitchCount } = props;
  const { state } = chartInfo;
  const { onTermTypeChange, onDatasetSwitch } = useChartStateAction(
    props.chartInfo,
  );
  return (
    <>
      <VFlex g={6}>
        <Pane title="Data set (row)">
          <ChartDimensionSelect
            switchFlag={switchCount}
            key={chartInfo.state.type}
            chartInfo={chartInfo}
            allowTop={false}
          />
        </Pane>
        <CenterFlex>
          <Button
            variant="ghost"
            leftIcon={<RotateCcwIcon style={{ rotate: '90deg' }} />}
            disabled={state.series.length > 1}
            tooltip={
              state.series.length > 1
                ? 'Remove additional data sets to switch visualization types'
                : undefined
            }
            onClick={() => {
              onDatasetSwitch();
              setSwitchCount((prev) => prev + 1);
            }}
          >
            Switch
          </Button>
        </CenterFlex>
        <ChartSeriesManager chartInfo={chartInfo} />
      </VFlex>
      <Pane title="Filter">
        <ChartDataFilter chartInfo={chartInfo} />
      </Pane>
      <Pane
        title="Date range"
        titleAction={
          <div onClick={(e) => e.stopPropagation()}>
            <Radio.Group
              value={state.target.term.type}
              onChange={(e) =>
                onTermTypeChange(e.target.value as CHART_TERM_TYPE)
              }
              options={ChartTermTypeMap.toArray()}
            />
          </div>
        }
      >
        <VFlex g={12}>
          {state.target.term.type === CHART_TERM_TYPE.PERIODIC && (
            <ChartTermSelect chartInfo={chartInfo} />
          )}
          {state.target.term.type === CHART_TERM_TYPE.FIXED && (
            <ChartTermCustomPicker chartInfo={chartInfo} />
          )}
          <ChartTermCompare chartInfo={chartInfo} />
          <TermValidateDisplay term={chartInfo.state.target.term} />
        </VFlex>
      </Pane>
    </>
  );
};
